import React, { useMemo } from "react";
import type { ARCDeployedTrendItem } from "../type";
import { colors } from "../../../utils/colors";
import { useApp } from "../../../contexts/AppContext";
import {
  EMPTY_ARC_DEPLOYED_STATE_MESSAGE,
  OFFSHORE_SERIES_NAME,
  ONSITE_SERIES_NAME,
} from "../../../utils/consts";
import { GenericLineChart, GenericLineChartSeries } from "./GenericLineChart";

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const formatMonthLabel = (year: number, month: number) =>
  `${MONTH_SHORT[month - 1] ?? month} ${year}`;

const buildChartData = (series: ARCDeployedTrendItem[]) => {
  const categories = series.map((p) => formatMonthLabel(p.year, p.month));
  const chartSeries: GenericLineChartSeries[] = [
    {
      name: ONSITE_SERIES_NAME,
      color: colors.chart_blue,
      data: series.map((p) => p.onsite),
    },
    {
      name: OFFSHORE_SERIES_NAME,
      color: colors.chart_gray,
      data: series.map((p) => p.offshore),
    },
  ];
  return { categories, series: chartSeries };
};

const formatYaxis = (rate: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  }).format(rate);

const formatRate = (rate: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(rate);

export interface ARCDeployedCostLineChartProps {
  title?: string;
  loading?: boolean;
  series: ARCDeployedTrendItem[];
}

export const ARCDeployedCostLineChart: React.FC<
  ARCDeployedCostLineChartProps
> = ({ title, loading = false, series }) => {
  const { drawerOpen } = useApp();

  const { categories, series: chartSeries } = useMemo(
    () => buildChartData(series),
    [series],
  );

  return (
    <GenericLineChart
      title={title}
      categories={categories}
      series={chartSeries}
      loading={loading}
      height={400}
      minHeight={400}
      emptyMessage={EMPTY_ARC_DEPLOYED_STATE_MESSAGE}
      showLegend={true}
      showEndLabels={false}
      xAxisLabelColor={colors.black}
      xAxisLabelFontSize="0.625rem"
      xAxisLabelFontWeight={500}
      yAxisLabelColor={colors.chart_gray}
      yAxisLabelFontSize="0.6875rem"
      xAxisRotateThreshold={5}
      tooltipValueFormatter={formatRate}
      yAxisValueFormatter={formatYaxis}
      resizeTrigger={drawerOpen}
    />
  );
};
